import React, { useState } from "react";
import { Link } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { CalendarDays, MapPin, Users } from "lucide-react";
import { api, errorMessage, Wallet } from "../lib/api";
import { money } from "../lib/format";
import { Button } from "../components/ui/button";

export interface TastingEvent { id: string; title: string; description: string; starts_at: string; venue: string; price_pence: number; capacity: number; image?: string | null; places_left: number; places_taken: number; my_booking?: { id: string; places: number; total_pence: number } | null }

const when = (iso: string) => new Date(iso).toLocaleString("en-GB", { weekday: "long", day: "numeric", month: "long", hour: "2-digit", minute: "2-digit" });

export default function EventsPage() {
  const qc = useQueryClient();
  const { data: events, isLoading, isError } = useQuery<TastingEvent[]>({ queryKey: ["events"], queryFn: () => api.get("/events").then((r) => r.data) });
  const { data: wallet } = useQuery<Wallet>({ queryKey: ["wallet"], queryFn: () => api.get("/wallet").then((r) => r.data) });
  const [places, setPlaces] = useState<Record<string, number>>({});
  const refresh = () => ["events", "wallet", "ledger"].forEach((k) => qc.invalidateQueries({ queryKey: [k] }));

  const book = async (e: TastingEvent) => {
    try {
      await api.post(`/events/${e.id}/book`, { places: places[e.id] || 1 });
      toast.success(`Booked — ${e.title}`);
      refresh();
    } catch (err) { toast.error(errorMessage(err)); }
  };
  const cancel = async (e: TastingEvent) => {
    try { await api.post(`/events/${e.id}/cancel`); toast.success("Booking cancelled — credit returned to your cellar"); refresh(); } catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div data-testid="events-page">
      <div className="eyebrow">Tastings in Derby</div>
      <h1 className="mt-3 font-serif text-4xl font-medium tracking-tight sm:text-5xl">Come and taste with us</h1>
      <p className="mt-3 max-w-xl text-sm text-neutral-600">Small, seated tastings at the shop, led by the family. Places are paid from your cellar and refunded there if you cancel.</p>
      {isLoading && <div className="mt-10 grid gap-px md:grid-cols-2" data-testid="events-loading">{[0, 1].map((i) => <div key={i} className="h-64 animate-pulse border border-border bg-[var(--parchment)]/60" />)}</div>}
      {isError && <p className="mt-12 border border-dashed border-border p-10 text-center text-sm text-[var(--wine)]" data-testid="events-error">We couldn't load the tastings just now — please try again shortly.</p>}
      {events && events.length === 0 && <p className="mt-12 border border-dashed border-border p-10 text-center text-sm text-neutral-500" data-testid="events-empty">No tastings scheduled just now — we will let you know.</p>}
      <div className="mt-10 grid gap-px md:grid-cols-2">
        {(events || []).map((e, i) => {
          const n = places[e.id] || 1;
          return (
            <article key={e.id} className="border border-border p-8 rise" style={{ animationDelay: `${i * 60}ms` }} data-testid={`event-card-${e.id}`}>
              <div className="eyebrow">{e.price_pence ? money(e.price_pence) + " per place" : "No charge"}</div>
              <h2 className="mt-2 font-serif text-2xl leading-tight sm:text-3xl">{e.title}</h2>
              <ul className="mt-4 space-y-1 text-sm text-neutral-600">
                <li className="flex items-center gap-2"><CalendarDays className="h-4 w-4" strokeWidth={1.25} />{when(e.starts_at)}</li>
                <li className="flex items-center gap-2"><MapPin className="h-4 w-4" strokeWidth={1.25} />{e.venue}</li>
                <li className="flex items-center gap-2"><Users className="h-4 w-4" strokeWidth={1.25} /><span data-testid={`event-places-${e.id}`}>{e.places_left} of {e.capacity} places left</span></li>
              </ul>
              {e.description && <p className="mt-4 text-sm leading-relaxed text-neutral-700">{e.description}</p>}
              <div className="mt-6">
                {e.my_booking ? (
                  <div className="flex flex-wrap items-center gap-4" data-testid={`event-booked-${e.id}`}>
                    <span className="bg-[var(--moss)] px-2 py-1 text-[10px] uppercase tracking-widest text-[var(--paper)]">Booked · {e.my_booking.places} place{e.my_booking.places > 1 ? "s" : ""}</span>
                    <button onClick={() => cancel(e)} className="text-sm underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`event-cancel-${e.id}`}>Cancel booking</button>
                  </div>
                ) : e.places_left === 0 ? (
                  <span className="text-sm text-[var(--wine)]">Fully booked</span>
                ) : (
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center border border-border">
                      <button onClick={() => setPlaces({ ...places, [e.id]: Math.max(1, n - 1) })} disabled={n <= 1} className="px-3 py-2 hover:bg-[var(--parchment)] disabled:opacity-40" data-testid={`event-dec-${e.id}`}>−</button>
                      <span className="w-8 text-center text-sm tabular-nums" data-testid={`event-qty-${e.id}`}>{n}</span>
                      <button onClick={() => setPlaces({ ...places, [e.id]: Math.min(e.places_left, n + 1) })} disabled={n >= e.places_left} className="px-3 py-2 hover:bg-[var(--parchment)] disabled:opacity-40" data-testid={`event-inc-${e.id}`}>+</button>
                    </div>
                    {wallet && wallet.balance_pence < e.price_pence * n ? (
                      <Link to="/cellar?tab=topup" className="text-sm underline underline-offset-4 hover:text-[var(--wine)]" data-testid={`event-topup-${e.id}`}>Insufficient cellar credit — top up</Link>
                    ) : (
                      <Button onClick={() => book(e)} className="rounded-none px-6 hover-lift" data-testid={`event-book-${e.id}`}>
                        {e.price_pence ? `Book · ${money(e.price_pence * n)} from cellar` : "Reserve my place"}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
}
